import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IAttendeesLiveFormContainerProps } from './attendees-live-form.container';
import { IAttendeeOrganizationDTO } from '../../../../../api/attendees.dao';
import { IAppsyncSubscription } from '../../../../../infrastructure/utils/appsync.utils';
import { ParticipantsLiveFormJoinedTabViewModel } from './participants-live-form/partisipants-live-form-joined-tab.viewmodel';
import { ParticipantsLiveFormRegistrantsTabViewModel } from './participants-live-form/partisipants-live-form-registrants-tab.viewmodel';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';
import { CONTROL_PAGE_TRACKING_EVENTS, OpenFrom, TAttendeeInfoPopoverTrackingValues, TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';

export enum ParticipantsLiveFormTabs {
    JOINED = 'Joined',
    REGISTRANTS = 'Registrants',
}

export type TAttendeesLiveFormVM = ParticipantsLiveFormJoinedTabViewModel;

export default class AttendeesLiveFormVM {
    public presenterOrganizations: string[];
    public startDateTime: Date;
    public endDateTime: Date;
    public eventTimezone: string;
    @mobx.observable
    public total: number;
    public subscription: IAppsyncSubscription;
    @mobx.observable
    public activeTab: ParticipantsLiveFormTabs;
    @mobx.observable
    public isTypesForMeetingsWithCounters: boolean;

    private eventId: number;
    private accountId: number;
    private meetingId: number;
    private registrantsCount: number;
    private joinedViewModel: ParticipantsLiveFormJoinedTabViewModel;
    private registrantsViewModel: ParticipantsLiveFormRegistrantsTabViewModel;

    constructor(
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) { }

    public get joinedTabVM(): ParticipantsLiveFormJoinedTabViewModel { return this.joinedViewModel; }
    public get registrantsTabVM(): ParticipantsLiveFormRegistrantsTabViewModel { return this.registrantsViewModel; }
    public get isMeetingsWithCounters(): boolean { return this.isTypesForMeetingsWithCounters; }
    public get registrantsCounter(): number { return this.registrantsCount; }
    public get meetingID(): string { return this.meetingId.toString(); }

    @mobx.action
    public initialize(formProps: IAttendeesLiveFormContainerProps): void {
        const {
            eventId,
            accountId,
            meetingId,
            presenters,
            startDateTime,
            endDateTime,
            eventTimezone,
            isTypesForMeetingsWithCounters,
            registrantsCount,
        } = formProps;

        this.eventId = eventId;
        this.accountId = accountId;
        this.meetingId = meetingId;
        this.registrantsCount = registrantsCount;
        this.presenterOrganizations = [...new Set(presenters.map((el: IAttendeeOrganizationDTO) => el.name))];
        this.startDateTime = startDateTime;
        this.endDateTime = endDateTime;
        this.eventTimezone = eventTimezone;
        this.activeTab = ParticipantsLiveFormTabs.JOINED;
        this.isTypesForMeetingsWithCounters = isTypesForMeetingsWithCounters;

        this.trackMixpanelEvent(CONTROL_PAGE_TRACKING_EVENTS.OPEN_PARTICIPANTS_POPUP);

        this.joinedViewModel = new ParticipantsLiveFormJoinedTabViewModel(formProps);
        this.registrantsViewModel = new ParticipantsLiveFormRegistrantsTabViewModel(formProps);
    }

    @mobx.action
    public setActiveTab(tab: ParticipantsLiveFormTabs): void {
        this.activeTab = tab;
        const event = tab === ParticipantsLiveFormTabs.JOINED
            ? CONTROL_PAGE_TRACKING_EVENTS.JOINED_TAB_PARTICIPANTS_POPUP
            : CONTROL_PAGE_TRACKING_EVENTS.REGISTRANTS_TAB_PARTICIPANTS_POPUP;

        this.trackMixpanelEvent(event);
    }

    public isRegistrantsActiveTab(): boolean {
        return this.activeTab === ParticipantsLiveFormTabs.REGISTRANTS;
    }

    public trackMixpanelEvent(event: CONTROL_PAGE_TRACKING_EVENTS): void {
        this.trackingService.get().track(event, {
            'Event ID': this.eventId.toString(),
            'Event Name': this.globalStorage.getEvent()?.name.toString(),
            'Account ID': this.accountId.toString(),
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Meeting ID': this.meetingId.toString()
        });
    }

    public trackCopyAttendeeInfo = (propName: TAttendeeInfoPopoverTrackingValues): void => {
        this.trackingService.get().track(
            TRACKING_EVENTS.COPY_ATTENDEE_NAME_FROM_POPOVER,
            {
                [propName]: OpenFrom.RegistrantsListInParticipantPopup
            }
        );
    };
}
